// src/pages/analytics/marginMode.tsx
//
// Margin % / Markup % / Both toggle shared by the GP Analysis and Margins tabs.
//   Margin % = GP ÷ sales   (what the tabs always showed as "GP %")
//   Markup % = GP ÷ cost
// The choice is remembered per browser so it carries across tabs.

import { useState } from 'react';

export type PctMode = 'margin' | 'markup' | 'both';

const KEY = 'analytics.pctMode';
const MODES: { value: PctMode; label: string }[] = [
  { value: 'margin', label: 'Margin %' },
  { value: 'markup', label: 'Markup %' },
  { value: 'both',   label: 'Both' },
];

function load(): PctMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'markup' || v === 'both' ? v : 'margin';
  } catch {
    return 'margin';
  }
}

export function usePctMode(): [PctMode, (m: PctMode) => void] {
  const [mode, setMode] = useState<PctMode>(load);
  const set = (m: PctMode) => {
    setMode(m);
    try { localStorage.setItem(KEY, m); } catch { /* private window */ }
  };
  return [mode, set];
}

export const showMargin = (m: PctMode) => m !== 'markup';
export const showMarkup = (m: PctMode) => m !== 'margin';

/** Markup % = GP ÷ cost; null when there is no cost. */
export function markupPct(gp: number, cost: number): number | null {
  return cost > 0 ? (gp / cost) * 100 : null;
}

const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

export function PctModeToggle({ mode, onChange }: { mode: PctMode; onChange: (m: PctMode) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Show</span>
      <div className="flex gap-1">
        {MODES.map(m => (
          <button key={m.value} onClick={() => onChange(m.value)} className={pill(mode === m.value)}
            title={m.value === 'markup' ? 'GP ÷ cost' : m.value === 'margin' ? 'GP ÷ sales' : 'Margin % and markup %'}>
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}
